import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './aiSparkButton.css';
import SparkMark from './AiSparkMark';

const HOVER_INTENT_MS = 120;
const canHover = () => typeof window === 'undefined' || typeof window.matchMedia !== 'function' || window.matchMedia('(hover: hover)').matches;

const STATES = ['rest', 'suggestion', 'thinking', 'done', 'open'];

/**
 * CrewVex AI button (KAN-991). Pure presentation: placement lives in AiFloatingFab.
 * variant 'web' widens into a labelled pill on mouse hover (no shortcut hint — none is wired).
 */
const AiSparkButton = forwardRef(function AiSparkButton(
  { size = 56, variant = 'round', state = 'rest', ariaLabel, hoverLabel, onClick, style },
  ref,
) {
  const mark = Math.round(size * 0.58);
  const safeState = STATES.includes(state) ? state : 'rest';
  const web = variant === 'web';
  const labelRef = useRef(null);
  const timerRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [labelW, setLabelW] = useState(0);
  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  useEffect(() => clearTimer, []);
  // Fit the pill to its own label (any language): re-measure when the text changes.
  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!web || !el) return undefined;
    const measure = () => setLabelW(Math.ceil(el.scrollWidth || el.getBoundingClientRect().width || 0));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [web, hoverLabel]);
  const onEnter = useCallback(() => {
    if (!web || !canHover()) return;
    clearTimer();
    timerRef.current = setTimeout(() => { timerRef.current = null; setExpanded(true); }, HOVER_INTENT_MS);
  }, [web]);
  const collapse = useCallback(() => { clearTimer(); setExpanded(false); }, []);
  // Leaving with the mouse keeps the pill open while keyboard focus-visible still holds it.
  const onLeave = useCallback((e) => {
    let focusVisible = false;
    try { focusVisible = e.currentTarget.matches(':focus-visible'); } catch (err) { focusVisible = false; }
    if (focusVisible) { clearTimer(); return; }
    collapse();
  }, [collapse]);
  const onFocus = useCallback((e) => {
    if (!web) return;
    let visible = false;
    try { visible = e.target.matches(':focus-visible'); } catch (err) { visible = false; }
    if (visible) { clearTimer(); setExpanded(true); }
  }, [web]);
  // The mark ends at (size + mark) / 2 (face padding centres it); 10px gap, then the label (its own 20px end padding).
  const openW = Math.round((size + mark) / 2) + 10 + labelW;
  const isOpenPill = web && expanded && labelW > 0;
  return (
    <button
      ref={ref}
      type="button"
      data-cvx-press
      data-cvx-ai-button
      data-state={safeState}
      aria-label={ariaLabel}
      onClick={onClick}
      onMouseEnter={web ? onEnter : undefined}
      onMouseLeave={web ? onLeave : undefined}
      onFocus={web ? onFocus : undefined}
      onBlur={web ? collapse : undefined}
      className={`cvx-ais cvx-ais--${safeState}${web ? ' cvx-ais--web' : ''}${isOpenPill ? ' cvx-ais--expanded' : ''}`}
      style={{ width: isOpenPill ? openW : size, height: size, '--cvx-ais-open-w': `${openW}px`, '--cvx-ais-s': `${size}px`, '--cvx-ais-m': `${mark}px`, ...style }}
    >
      <span className="cvx-ais__ripple" />
      <span className="cvx-ais__face">
        <span className="cvx-ais__aur" />
        <SparkMark size={mark} />
        {web && hoverLabel ? <span ref={labelRef} className="cvx-ais__label" aria-hidden="true">{hoverLabel}</span> : null}
      </span>
    </button>
  );
});

export default AiSparkButton;
